import React, { useState, useCallback } from "react";
import { Input, Button, List, Tag, message, Divider } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { validateUnionMemberName } from "./SchemaValidation";

interface FbsUnionInfo {
  name: string;
  namespace: string;
  members: string[];
}

interface UnionEditorProps {
  unionNode: FbsUnionInfo;
  onAddMember: (name: string) => void;
}

function UnionEditor({ unionNode, onAddMember }: UnionEditorProps) {
  const [newMember, setNewMember] = useState("");

  const handleAdd = useCallback(() => {
    const trimmed = newMember.trim();
    if (!trimmed) return;

    const errors = validateUnionMemberName(trimmed);
    if (errors.length > 0) {
      message.error(errors[0].message);
      return;
    }

    if (unionNode.members.includes(trimmed)) {
      message.error(`"${trimmed}" already exists in this union`);
      return;
    }

    onAddMember(trimmed);
    setNewMember("");
    message.success(`Added "${trimmed}" to ${unionNode.name}`);
  }, [newMember, unionNode, onAddMember]);

  return (
    <div>
      <Divider orientation="left">
        Union: {unionNode.namespace}::{unionNode.name} -- {unionNode.members.length} members
      </Divider>

      <List
        size="small"
        bordered
        dataSource={unionNode.members}
        renderItem={(member, index) => (
          <List.Item>
            <span>
              <Tag color="blue">{index}</Tag>
              {member}
            </span>
          </List.Item>
        )}
        style={{ maxWidth: 500, marginBottom: 12 }}
      />

      <div style={{ display: "flex", gap: 8, maxWidth: 500 }}>
        <Input
          placeholder="NewUnionDiscriminantName (PascalCase)"
          value={newMember}
          onChange={(e) => setNewMember(e.target.value)}
          size="small"
          onPressEnter={handleAdd}
        />
        <Button
          type="primary"
          size="small"
          icon={<FontAwesomeIcon icon={faPlus} style={{ marginRight: 4 }} />}
          onClick={handleAdd}
          disabled={!newMember.trim()}
        >
          Add Member
        </Button>
      </div>
    </div>
  );
}

export default UnionEditor;
